import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export const useWebSocket = (token) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const messageHandlersRef = useRef(new Set());

  const connect = useCallback(() => {
    if (!token || !WS_URL) return;

    try {
      // Auth via `?token=` query param (server accepts both this and a first-
      // message handshake). This keeps the URL PATH clean of secrets so proxy
      // access logs don't capture the JWT — only the query string does, which
      // proxies typically strip or configure to not log.
      const ws = new WebSocket(`${WS_URL}/ws?token=${encodeURIComponent(token)}`);

      ws.onopen = () => {
        setIsConnected(true);

        // Start ping interval to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong') {
            setLastMessage(data);
            messageHandlersRef.current.forEach(handler => handler(data));
          }
        } catch {
          // Ignore malformed frames
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);

        if (ws._pingInterval) {
          clearInterval(ws._pingInterval);
        }

        // Reconnect after 3 seconds unless intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      ws.onerror = () => {
        // Errors surface via onclose; nothing actionable here in prod
      };

      wsRef.current = ws;
    } catch {
      // Constructor failed — likely bad URL. Nothing to do.
    }
  }, [token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      if (wsRef.current._pingInterval) {
        clearInterval(wsRef.current._pingInterval);
      }
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const sendTyping = useCallback((matchId, isTyping) => {
    sendMessage({
      type: 'typing',
      match_id: matchId,
      is_typing: isTyping
    });
  }, [sendMessage]);

  const markAsRead = useCallback((matchId) => {
    sendMessage({
      type: 'read',
      match_id: matchId
    });
  }, [sendMessage]);

  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.add(handler);
    return () => messageHandlersRef.current.delete(handler);
  }, []);

  useEffect(() => {
    if (token) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    sendTyping,
    markAsRead,
    addMessageHandler,
    reconnect: connect
  };
};

export default useWebSocket;
